import { Form, Input, Select, Button, InputNumber, Upload } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PositionSelect } from "../../PositionSelect/PositionSelect";
import { useHeadshot } from "@hooks/useHeadshot";
import { uploadPlayerHeadshot } from "@store/actions/team.actions";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import "./PlayerForm.css";

const { Option } = Select;

export const PlayerForm = ({
  defaultValue = {},
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const [form] = Form.useForm();

  const { allPositions, allPlayers, sport } = useSelector(({ team }) => team);
  const [pending, setPending] = useState(false);

  const dispatch = useDispatch();
  const isEdit = !!defaultValue?.player_id;
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);

  // Derive the headshot from the live Redux roster (refreshed by the upload's
  // getTeamPlayers refetch), not the POST response — same source as the table,
  // so the preview and the row update together after an upload.
  const livePlayer = isEdit
    ? allPlayers.find((p) => p.player_id === defaultValue.player_id)
    : null;
  const headshotImageUrl =
    livePlayer?.headshot_image_url ?? defaultValue?.headshot_image_url ?? null;
  const headshotPlayer = useMemo(
    () => ({
      headshot_image_url: headshotImageUrl,
      first_name: defaultValue?.first_name,
      last_name: defaultValue?.last_name,
    }),
    [headshotImageUrl, defaultValue]
  );
  const headshotSrc = useHeadshot(headshotPlayer);
  const initials = `${defaultValue?.first_name?.[0] ?? ""}${
    defaultValue?.last_name?.[0] ?? ""
  }`.toUpperCase();

  const handleHeadshotUpload = async (file) => {
    setUploadingHeadshot(true);
    await dispatch(uploadPlayerHeadshot(defaultValue.player_id, file));
    setUploadingHeadshot(false);
  };

  const beforeHeadshotUpload = (file) => {
    if (file.type !== "image/png") {
      message.error("Headshot must be a PNG image");
      return Upload.LIST_IGNORE;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("Headshot must be 5MB or smaller");
      return Upload.LIST_IGNORE;
    }
    handleHeadshotUpload(file);
    return false;
  };

  const handleSubmit = async () => {
    setPending(true);
    try {
      const values = await form.validateFields();
      await onSubmit({ ...defaultValue, ...values });
      handleCancel();
    } catch (e) {
      console.log(e);
      return;
    }
    setPending(false);
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  useEffect(() => {
    form.resetFields(["first_name", "Hello"]);
  }, []);

  const checkFullName = (getFieldValue) => {
    const firstName = getFieldValue("first_name")?.trim() || "";
    const lastName = getFieldValue("last_name")?.trim() || "";
    const foundPlayers = allPlayers.filter(
      ({ first_name, last_name, player_id }) => {
        return (
          first_name === firstName &&
          last_name === lastName &&
          player_id !== defaultValue?.player_id
        );
      }
    );
    return foundPlayers.length === 0;
  };

  const checkEmail = (getFieldValue) => {
    const email = getFieldValue("email")?.trim() || "";
    const foundPlayers = allPlayers.filter(
      ({ email: playerEmail, player_id }) => {
        return playerEmail === email && player_id !== defaultValue?.player_id;
      }
    );
    return foundPlayers.length === 0;
  };

  const nameValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      if (checkFullName(getFieldValue)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Same name already exists"));
    },
  });

  const emailValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      if (checkEmail(getFieldValue)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Same email already exists"));
    },
  });

  const positionValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      if (
        !getFieldValue("position") ||
        allPositions.includes(getFieldValue("position"))
      ) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Position is not recognized"));
    },
  });

  useEffect(() => {
    if (defaultValue === null) form.resetFields();
    else {
      form.setFieldsValue(defaultValue);
      form.validateFields();
    }
  }, [defaultValue]);

  return (
    <Form
      form={form}
      name="player-form"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 18 }}
    >
      {isEdit && (
        <Form.Item label="Photo">
          <div className="player-form-headshot-row">
            <div className="player-form-headshot">
              {headshotSrc ? <img src={headshotSrc} alt="" /> : <span>{initials}</span>}
            </div>
            <div className="player-form-headshot-controls">
              <Upload
                accept="image/png"
                showUploadList={false}
                beforeUpload={beforeHeadshotUpload}
                disabled={uploadingHeadshot}
              >
                <Button icon={<EditOutlined />} loading={uploadingHeadshot}>
                  Edit photo
                </Button>
              </Upload>
              <span className="player-form-headshot-hint">
                Must be .png, up to 5 MB.
                <br />
                Transparent background recommended.
              </span>
            </div>
          </div>
        </Form.Item>
      )}
      <Form.Item
        label="First Name"
        name="first_name"
        rules={[
          { required: true, message: "Please input first name" },
          nameValidator,
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Last Name"
        name="last_name"
        rules={[
          { required: true, message: "Please input last name" },
          nameValidator,
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Position"
        name="position"
        rules={[
          { required: true, message: "Please select position" },
          positionValidator,
        ]}
      >
        <PositionSelect
          options={allPositions}
          sport={sport}
          value={form.getFieldValue("position")}
          handleChange={(value) => form.setFieldsValue({ position: value })}
        />
      </Form.Item>
      <Form.Item
        label="Jersey Number"
        name="number"
        rules={[{ required: true, message: "Please input jersey number" }]}
      >
        <Input type="number" min={0} />
      </Form.Item>
      <Form.Item
        label="Email"
        name="email"
        rules={[
          { type: "email", message: "Email is not a valid email" },
          emailValidator,
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 16, span: 8 }}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          type="primary"
          style={{ margin: "0 8px" }}
        >
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};
